import { useEffect, useState, type FormEvent } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Megaphone } from 'lucide-react';
import { useTranslation } from '../i18n/useTranslation';
import { Badge, Button, Card, Checkbox, EmptyState, Grid, Heading, Inline, Input, Select, Stack, Text, Textarea } from '../components/atoms';
import type { BadgeTone } from '../components/atoms/Badge/Badge.types';
import { Alert } from '../components/atoms/Alert/Alert';
import { LiveRegion } from '../components/atoms/LiveRegion/LiveRegion';
import { LoadErrorState } from '../components/feedback/LoadErrorState/LoadErrorState';
import { AppLayout } from '../components/templates/AppLayout/AppLayout';
import { PageContent } from '../components/templates/PageContent/PageContent';
import { PageHeader } from '../components/templates/PageHeader/PageHeader';
import { Section } from '../components/templates/Section/Section';
import { Header } from '../components/layout/Header';
import { Sidebar } from '../components/Sidebar';
import { AdminArea } from '../components/admin/AdminArea';
import { AdminTable } from '../components/admin/AdminTable';
import { AdminConfirmDialog, type AdminConfirmRequest } from '../components/admin/AdminConfirmDialog';
import { ReleaseNotePreview } from '../components/admin/ReleaseNotePreview';
import { formatDate, formatNumber } from '../components/admin/adminFormat';
import { ApiError } from '../lib/apiClient';
import {
  combineReleaseNotes,
  createReleaseNote,
  dissolveCombinedReleaseNote,
  fetchReleaseNote,
  fetchReleaseNotes,
  regenerateReleaseNote,
  sendReleaseNote,
  sendTestReleaseNote,
  setReleaseNoteInApp,
  updateReleaseNote,
  type ReleaseNote,
  type ReleaseNoteGenerationStatus,
  type ReleaseNoteStatus,
  type ReleaseNoteText,
} from '../services/releaseNotesService';
import './AdminReleaseNotesPage.css';

const LIST_KEY = ['admin', 'release-notes'] as const;
const EMPTY_TEXT: ReleaseNoteText = { titleDe: '', bodyDe: '', titleEn: '', bodyEn: '' };
type TextErrors = Partial<Record<keyof ReleaseNoteText, string>>;

function statusTone(status: ReleaseNoteStatus): BadgeTone {
  if (status === 'sent') return 'success';
  if (status === 'sending') return 'info';
  return 'neutral';
}

function generationTone(status: ReleaseNoteGenerationStatus): BadgeTone {
  if (status === 'ai') return 'ai';
  if (status === 'failed') return 'danger';
  if (status === 'pending') return 'warning';
  return 'neutral';
}

/** Translated message for a failed admin action — never the raw backend text. */
function actionErrorKey(err: unknown): string {
  if (err instanceof ApiError) {
    switch (err.code) {
      case 'EMAIL_NOT_CONFIGURED':
        return 'adminArea.releaseNotes.errors.emailNotConfigured';
      case 'NOT_EDITABLE':
        return 'adminArea.releaseNotes.errors.notEditable';
      case 'ALREADY_SENDING':
        return 'adminArea.releaseNotes.errors.alreadySending';
      case 'NOT_READY':
        return 'adminArea.releaseNotes.errors.notReady';
      case 'INCOMPLETE':
        return 'adminArea.releaseNotes.errors.incomplete';
      case 'VERSION_EXISTS':
        return 'adminArea.releaseNotes.errors.versionExists';
      case 'NO_SOURCE_ITEMS':
        return 'adminArea.releaseNotes.errors.noSourceItems';
      case 'COMBINED':
        return 'adminArea.releaseNotes.errors.combined';
      case 'NOT_COMBINABLE':
        return 'adminArea.releaseNotes.errors.notCombinable';
      case 'NOT_COMBINED':
        return 'adminArea.releaseNotes.errors.notCombined';
      default:
        break;
    }
  }
  return 'adminArea.releaseNotes.errors.generic';
}

function useTextValidation() {
  const { t } = useTranslation();
  return (text: ReleaseNoteText): TextErrors => {
    const errors: TextErrors = {};
    if (text.titleDe.trim() === '') errors.titleDe = t('adminArea.releaseNotes.form.titleRequired');
    if (text.bodyDe.trim() === '') errors.bodyDe = t('adminArea.releaseNotes.form.bodyRequired');
    if (text.titleEn.trim() === '') errors.titleEn = t('adminArea.releaseNotes.form.titleRequired');
    if (text.bodyEn.trim() === '') errors.bodyEn = t('adminArea.releaseNotes.form.bodyRequired');
    return errors;
  };
}

interface TextFieldsProps {
  idPrefix: string;
  value: ReleaseNoteText;
  errors: TextErrors;
  disabled?: boolean;
  onChange: (next: ReleaseNoteText) => void;
}

/** Title + Markdown body per language. */
function TextFields({ idPrefix, value, errors, disabled = false, onChange }: TextFieldsProps) {
  const { t } = useTranslation();
  const field = (key: keyof ReleaseNoteText) => (e: { target: { value: string } }) =>
    onChange({ ...value, [key]: e.target.value });
  return (
    <Grid minItemWidth="md" gap="6">
      <Stack gap="3">
        <Heading level={4}>{t('adminArea.releaseNotes.form.german')}</Heading>
        <Input
          id={`${idPrefix}-title-de`}
          label={t('adminArea.releaseNotes.form.title')}
          value={value.titleDe}
          onChange={field('titleDe')}
          error={errors.titleDe}
          disabled={disabled}
          maxLength={200}
          fullWidth
        />
        <Textarea
          id={`${idPrefix}-body-de`}
          label={t('adminArea.releaseNotes.form.body')}
          helperText={t('adminArea.releaseNotes.form.bodyHelp')}
          value={value.bodyDe}
          onChange={field('bodyDe')}
          error={errors.bodyDe}
          disabled={disabled}
          rows={10}
          maxLength={10000}
        />
      </Stack>
      <Stack gap="3">
        <Heading level={4}>{t('adminArea.releaseNotes.form.english')}</Heading>
        <Input
          id={`${idPrefix}-title-en`}
          label={t('adminArea.releaseNotes.form.title')}
          value={value.titleEn}
          onChange={field('titleEn')}
          error={errors.titleEn}
          disabled={disabled}
          maxLength={200}
          fullWidth
        />
        <Textarea
          id={`${idPrefix}-body-en`}
          label={t('adminArea.releaseNotes.form.body')}
          helperText={t('adminArea.releaseNotes.form.bodyHelp')}
          value={value.bodyEn}
          onChange={field('bodyEn')}
          error={errors.bodyEn}
          disabled={disabled}
          rows={10}
          maxLength={10000}
        />
      </Stack>
    </Grid>
  );
}

/** A release that can be rolled into a combined e-mail: an unsent draft, not combined, not a combination. */
export function isCombinable(note: ReleaseNote): boolean {
  const reached = note.deliveries.sent + note.deliveries.failed + note.deliveries.sending > 0;
  return (
    note.status === 'draft' &&
    !reached &&
    note.combinedInto === null &&
    note.combinedFrom.length === 0 &&
    note.generationStatus !== 'pending'
  );
}

function versionLabel(note: ReleaseNote | undefined, t: (key: string, options?: Record<string, unknown>) => string): string {
  if (!note) return '';
  return note.appVersion ? `${t('appVersion.label', { version: note.appVersion })} (${note.version})` : note.version;
}

function textOf(note: ReleaseNote): ReleaseNoteText {
  return { titleDe: note.titleDe, bodyDe: note.bodyDe, titleEn: note.titleEn, bodyEn: note.bodyEn };
}

function sameText(a: ReleaseNoteText, b: ReleaseNoteText): boolean {
  return a.titleDe === b.titleDe && a.bodyDe === b.bodyDe && a.titleEn === b.titleEn && a.bodyEn === b.bodyEn;
}

// ─── Create (manual note) ───────────────────────────────────────────────────

function CreateReleaseNoteForm({ onCreated, onCancel }: { onCreated: (note: ReleaseNote) => void; onCancel: () => void }) {
  const { t } = useTranslation();
  const validate = useTextValidation();
  const [text, setText] = useState<ReleaseNoteText>(EMPTY_TEXT);
  const [errors, setErrors] = useState<TextErrors>({});
  const [saving, setSaving] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  async function onSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    const found = validate(text);
    setErrors(found);
    if (Object.keys(found).length > 0) return;
    setSaving(true);
    setFailure(null);
    try {
      onCreated(await createReleaseNote(text));
    } catch (err: unknown) {
      setFailure(t(actionErrorKey(err)));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card padding="lg">
      <form onSubmit={(e) => void onSubmit(e)} noValidate>
        <Stack gap="4">
          <Heading level={3}>{t('adminArea.releaseNotes.create.title')}</Heading>
          <Text size="sm" color="muted">{t('adminArea.releaseNotes.create.description')}</Text>
          <TextFields idPrefix="new-note" value={text} errors={errors} onChange={setText} />
          {failure !== null && <Alert compact>{failure}</Alert>}
          <Inline gap="2">
            <Button type="submit" variant="secondary" isLoading={saving}>
              {t('adminArea.releaseNotes.create.submit')}
            </Button>
            <Button type="button" variant="ghost" onClick={onCancel} disabled={saving}>
              {t('common.cancel')}
            </Button>
          </Inline>
        </Stack>
      </form>
    </Card>
  );
}

// ─── Detail ─────────────────────────────────────────────────────────────────

interface ReleaseNoteDetailProps {
  id: string;
  /** Every note of the list — names the releases of a combination. */
  notes: readonly ReleaseNote[];
  onOpen: (id: string) => void;
  onClose: () => void;
}

function ReleaseNoteDetail({ id, notes, onOpen, onClose }: ReleaseNoteDetailProps) {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const validate = useTextValidation();
  const detail = useQuery({
    queryKey: [...LIST_KEY, id],
    queryFn: () => fetchReleaseNote(id),
    // While the background job sends (or the draft is generated), refresh the progress.
    refetchInterval: (query) => {
      const status = query.state.data?.note;
      return status && (status.status === 'sending' || status.generationStatus === 'pending') ? 3000 : false;
    },
  });
  const [text, setText] = useState<ReleaseNoteText>(EMPTY_TEXT);
  const [errors, setErrors] = useState<TextErrors>({});
  const [busy, setBusy] = useState<'save' | 'regenerate' | 'test' | 'inApp' | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [notice, setNotice] = useState('');
  const [testLanguage, setTestLanguage] = useState<'de' | 'en'>('de');
  const [confirm, setConfirm] = useState<AdminConfirmRequest | null>(null);

  const note = detail.data?.note;
  useEffect(() => {
    if (note) setText(textOf(note));
    // Reset the form only when another note / a new server version arrives.
  }, [note?.id, note?.updatedAt]); // eslint-disable-line react-hooks/exhaustive-deps

  async function refresh(): Promise<void> {
    await queryClient.invalidateQueries({ queryKey: LIST_KEY });
  }

  async function run(kind: 'save' | 'regenerate' | 'test' | 'inApp', action: () => Promise<unknown>, success: string): Promise<void> {
    setBusy(kind);
    setFailure(null);
    setNotice('');
    try {
      await action();
      setNotice(success);
      await refresh();
    } catch (err: unknown) {
      setFailure(t(actionErrorKey(err)));
    } finally {
      setBusy(null);
    }
  }

  if (detail.isPending) {
    return (
      <LiveRegion busy>
        <Text>{t('adminArea.releaseNotes.loading')}</Text>
      </LiveRegion>
    );
  }
  if (detail.isError || !detail.data || !note) {
    return <LoadErrorState titleLevel={3} title={t('adminArea.releaseNotes.loadError')} onRetry={() => void detail.refetch()} />;
  }

  const { pendingRecipients, email } = detail.data;
  const hasDeliveries = note.deliveries.sent + note.deliveries.failed + note.deliveries.sending > 0;
  const editable = note.status === 'draft' && !hasDeliveries && note.combinedInto === null;
  const combinedIn = note.combinedInto !== null ? notes.find((n) => n.id === note.combinedInto) : undefined;
  const dirty = !sameText(text, textOf(note));
  const sending = note.status === 'sending';
  const savedText = textOf(note);
  const textComplete = [savedText.titleDe, savedText.bodyDe, savedText.titleEn, savedText.bodyEn].every((v) => v.trim() !== '');
  const canSend =
    email.available && !sending && !dirty && note.generationStatus !== 'pending' && pendingRecipients > 0 && note.combinedInto === null;

  function save(): void {
    const found = validate(text);
    setErrors(found);
    if (Object.keys(found).length > 0) return;
    void run('save', () => updateReleaseNote(id, text), t('adminArea.releaseNotes.saved'));
  }

  function askRegenerate(): void {
    setConfirm({
      title: t('adminArea.releaseNotes.regenerate'),
      message: t('adminArea.releaseNotes.confirmRegenerate'),
      confirmLabel: t('adminArea.releaseNotes.regenerate'),
      onConfirm: async () => {
        await regenerateReleaseNote(id);
        setNotice(t('adminArea.releaseNotes.regenerated'));
        await refresh();
      },
    });
  }

  function askDissolve(): void {
    setConfirm({
      title: t('adminArea.releaseNotes.combine.dissolve'),
      message: t('adminArea.releaseNotes.combine.confirmDissolve', { count: note!.combinedFrom.length }),
      confirmLabel: t('adminArea.releaseNotes.combine.dissolve'),
      onConfirm: async () => {
        await dissolveCombinedReleaseNote(id);
        await refresh();
        onClose();
      },
    });
  }

  function askSend(): void {
    setConfirm({
      title: t('adminArea.releaseNotes.sendTitle'),
      message: t('adminArea.releaseNotes.confirmSend', { count: pendingRecipients, version: note!.version }),
      confirmLabel: t('adminArea.releaseNotes.sendConfirm'),
      onConfirm: async () => {
        await sendReleaseNote(id);
        setNotice(t('adminArea.releaseNotes.sendStarted'));
        await refresh();
      },
    });
  }

  return (
    <Stack gap="6">
      <Inline gap="2" align="center" wrap>
        <Badge tone={statusTone(note.status)}>{t(`adminArea.releaseNotes.status.${note.status}`)}</Badge>
        <Badge tone={generationTone(note.generationStatus)}>
          {t(`adminArea.releaseNotes.generation.${note.generationStatus}`)}
        </Badge>
        {note.appVersion && (
          <Text size="sm" weight="semibold" as="span">
            {t('appVersion.label', { version: note.appVersion })}
          </Text>
        )}
        <Text size="sm" color="muted" as="span">
          {note.version} · {formatDate(note.deployedAt ?? note.createdAt, i18n.language)}
        </Text>
      </Inline>

      {(hasDeliveries || sending) && (
        <LiveRegion>
          <Text>
            {t('adminArea.releaseNotes.progress', {
              sent: formatNumber(note.deliveries.sent, i18n.language),
              failed: formatNumber(note.deliveries.failed, i18n.language),
              sending: formatNumber(note.deliveries.sending, i18n.language),
            })}
          </Text>
        </LiveRegion>
      )}
      {note.combinedInto !== null && (
        <Alert
          tone="info"
          live="off"
          action={
            <Button variant="secondary" size="sm" onClick={() => onOpen(note.combinedInto!)}>
              {t('adminArea.releaseNotes.combine.openCombined')}
            </Button>
          }
        >
          {t('adminArea.releaseNotes.combine.partOf', { version: versionLabel(combinedIn, t) || note.combinedInto })}
        </Alert>
      )}
      {note.combinedFrom.length > 0 && (
        <Card padding="lg">
          <Stack gap="3">
            <Heading level={3}>{t('adminArea.releaseNotes.combine.includedTitle', { count: note.combinedFrom.length })}</Heading>
            <ul className="admin-release-notes__sources">
              {note.combinedFrom.map((sourceId) => (
                <li key={sourceId}>
                  <Text as="span" size="sm">
                    {versionLabel(notes.find((n) => n.id === sourceId), t) || sourceId}
                  </Text>
                </li>
              ))}
            </ul>
            {editable && (
              <div>
                <Button variant="ghost" onClick={askDissolve} disabled={busy !== null}>
                  {t('adminArea.releaseNotes.combine.dissolve')}
                </Button>
              </div>
            )}
          </Stack>
        </Card>
      )}
      {note.generationStatus === 'pending' && (
        <Alert tone="info" live="off" compact>{t('adminArea.releaseNotes.generating')}</Alert>
      )}

      <Card padding="lg">
        <Stack gap="4">
          <Heading level={3}>{t('adminArea.releaseNotes.textTitle')}</Heading>
          {!editable && <Text size="sm" color="muted">{t('adminArea.releaseNotes.readOnly')}</Text>}
          <TextFields idPrefix={`note-${note.id}`} value={text} errors={errors} disabled={!editable || busy !== null} onChange={setText} />
          {editable && (
            <Inline gap="2" wrap>
              <Button variant="secondary" onClick={save} isLoading={busy === 'save'} disabled={!dirty || busy !== null}>
                {t('adminArea.releaseNotes.save')}
              </Button>
              <Button variant="ghost" onClick={askRegenerate} disabled={busy !== null || note.sourceItems.length === 0}>
                {t('adminArea.releaseNotes.regenerate')}
              </Button>
            </Inline>
          )}
        </Stack>
      </Card>

      <Card padding="lg">
        <Stack gap="4">
          <Heading level={3}>{t('adminArea.releaseNotes.previewTitle')}</Heading>
          <Grid minItemWidth="md" gap="6">
            <Stack gap="2">
              <Text size="sm" color="muted">{t('adminArea.releaseNotes.form.german')}</Text>
              <ReleaseNotePreview title={text.titleDe} body={text.bodyDe} titleLevel={4} lang="de" />
            </Stack>
            <Stack gap="2">
              <Text size="sm" color="muted">{t('adminArea.releaseNotes.form.english')}</Text>
              <ReleaseNotePreview title={text.titleEn} body={text.bodyEn} titleLevel={4} lang="en" />
            </Stack>
          </Grid>
        </Stack>
      </Card>

      <Card padding="lg">
        <Stack gap="3">
          <Heading level={3}>{t('adminArea.releaseNotes.inApp.title')}</Heading>
          <Text size="sm" color="muted">{t('adminArea.releaseNotes.inApp.hint')}</Text>
          <Checkbox
            id={`in-app-${note.id}`}
            label={t('adminArea.releaseNotes.inApp.label')}
            checked={note.showInApp}
            disabled={busy !== null || (!note.showInApp && (dirty || !textComplete))}
            onChange={(e) => {
              const show = e.target.checked;
              void run(
                'inApp',
                () => setReleaseNoteInApp(id, show),
                show ? t('adminArea.releaseNotes.inApp.shown') : t('adminArea.releaseNotes.inApp.hidden'),
              );
            }}
          />
          {!note.showInApp && (dirty || !textComplete) && (
            <Text size="sm">{t(dirty ? 'adminArea.releaseNotes.inApp.saveFirst' : 'adminArea.releaseNotes.inApp.incomplete')}</Text>
          )}
        </Stack>
      </Card>

      <Card padding="lg">
        <Stack gap="4">
          <Heading level={3}>{t('adminArea.releaseNotes.sendSectionTitle')}</Heading>
          {dirty && <Text size="sm">{t('adminArea.releaseNotes.saveFirst')}</Text>}
          <Inline gap="2" align="end" wrap>
            <Select
              id={`test-language-${note.id}`}
              label={t('adminArea.releaseNotes.testLanguage')}
              value={testLanguage}
              onChange={(e) => setTestLanguage(e.target.value === 'en' ? 'en' : 'de')}
            >
              <option value="de">{t('adminArea.releaseNotes.form.german')}</option>
              <option value="en">{t('adminArea.releaseNotes.form.english')}</option>
            </Select>
            <Button
              variant="secondary"
              onClick={() => void run('test', () => sendTestReleaseNote(id, testLanguage), t('adminArea.releaseNotes.testSent'))}
              isLoading={busy === 'test'}
              disabled={!email.available || dirty || busy !== null}
            >
              {t('adminArea.releaseNotes.sendTest')}
            </Button>
          </Inline>
          <div>
            <Button variant="primary" onClick={askSend} disabled={!canSend || busy !== null}>
              {t('adminArea.releaseNotes.sendAll', { count: pendingRecipients })}
            </Button>
          </div>
          {pendingRecipients === 0 && !sending && (
            <Text size="sm" color="muted">{t('adminArea.releaseNotes.noRecipients')}</Text>
          )}
        </Stack>
      </Card>

      {note.sourceItems.length > 0 && (
        <Card padding="lg">
          <Stack gap="3">
            <Heading level={3}>{t('adminArea.releaseNotes.sourceTitle', { count: note.sourceItems.length })}</Heading>
            <ul className="admin-release-notes__sources">
              {note.sourceItems.map((item) => (
                <li key={item.number}>
                  <Text as="span" size="sm">
                    #{item.number} {item.title}
                  </Text>
                </li>
              ))}
            </ul>
          </Stack>
        </Card>
      )}

      {failure !== null && (
        <Alert onDismiss={() => setFailure(null)} dismissLabel={t('common.close')}>
          {failure}
        </Alert>
      )}
      <LiveRegion visuallyHidden>{notice}</LiveRegion>
      {notice !== '' && (
        <Alert tone="success" live="off" compact>
          {notice}
        </Alert>
      )}

      <div>
        <Button variant="ghost" onClick={onClose}>
          {t('adminArea.releaseNotes.backToList')}
        </Button>
      </div>

      <AdminConfirmDialog request={confirm} onClose={() => setConfirm(null)} />
    </Stack>
  );
}

// ─── Page ───────────────────────────────────────────────────────────────────

/**
 * Admin → Release Notes: drafts created automatically on every production
 * deploy (AI or fallback text from the merged PRs) or written by hand; edit
 * German/English text with a preview, send a test mail to yourself and send
 * the note to every confirmed product-update subscriber (in the background,
 * with progress). Backend: /api/v1/admin/release-notes.
 */
export function AdminReleaseNotesPage() {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const list = useQuery({ queryKey: LIST_KEY, queryFn: fetchReleaseNotes });
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  // Releases ticked for one combined e-mail.
  const [picked, setPicked] = useState<ReadonlySet<string>>(new Set());
  const [confirm, setConfirm] = useState<AdminConfirmRequest | null>(null);
  const [combineError, setCombineError] = useState<string | undefined>(undefined);

  const notes = list.data?.notes ?? [];
  const selected = notes.find((n) => n.id === selectedId) ?? null;
  const combinable = notes.filter(isCombinable);
  // Only what can still be combined counts (a picked release may have been sent meanwhile).
  const pickedIds = combinable.filter((n) => picked.has(n.id)).map((n) => n.id);

  function togglePicked(id: string, on: boolean): void {
    setPicked((current) => {
      const next = new Set(current);
      if (on) next.add(id);
      else next.delete(id);
      return next;
    });
  }

  function askCombine(): void {
    setCombineError(undefined);
    setConfirm({
      title: t('adminArea.releaseNotes.combine.confirmTitle'),
      message: t('adminArea.releaseNotes.combine.confirmMessage', { count: pickedIds.length }),
      confirmLabel: t('adminArea.releaseNotes.combine.confirmLabel'),
      onConfirm: async () => {
        try {
          const combined = await combineReleaseNotes(pickedIds);
          setPicked(new Set());
          await queryClient.invalidateQueries({ queryKey: LIST_KEY });
          setSelectedId(combined.id);
        } catch (err: unknown) {
          setCombineError(t(actionErrorKey(err)));
          throw err;
        }
      },
    });
  }

  return (
    <AppLayout
      header={<Header />}
      sidebar={<Sidebar />}
      main={
        <PageContent width="wide">
          <PageHeader title={t('adminArea.title')} description={t('adminArea.releaseNotes.description')} />
          <AdminArea>
            {list.data && !list.data.email.available && (
              <Alert tone="warning" live="off" title={t('adminArea.releaseNotes.emailNotConfiguredTitle')}>
                {t('adminArea.releaseNotes.emailNotConfigured')}
              </Alert>
            )}

            {selectedId !== null ? (
              <Section
                title={
                  selected?.appVersion
                    ? t('adminArea.releaseNotes.detailTitleVersioned', { version: selected.version, appVersion: selected.appVersion })
                    : t('adminArea.releaseNotes.detailTitle', { version: selected?.version ?? '' })
                }
              >
                <ReleaseNoteDetail id={selectedId} notes={notes} onOpen={setSelectedId} onClose={() => setSelectedId(null)} />
              </Section>
            ) : (
              <Section
                title={t('adminArea.releaseNotes.listTitle')}
                description={
                  list.data
                    ? t('adminArea.releaseNotes.subscribers', { count: list.data.subscriberCount })
                    : undefined
                }
                actions={
                  !creating ? (
                    <Button variant="secondary" onClick={() => setCreating(true)}>
                      {t('adminArea.releaseNotes.create.open')}
                    </Button>
                  ) : undefined
                }
              >
                <Stack gap="6">
                  {creating && (
                    <CreateReleaseNoteForm
                      onCancel={() => setCreating(false)}
                      onCreated={(note) => {
                        setCreating(false);
                        void queryClient.invalidateQueries({ queryKey: LIST_KEY });
                        setSelectedId(note.id);
                      }}
                    />
                  )}
                  {list.isPending && (
                    <LiveRegion busy>
                      <Text>{t('adminArea.releaseNotes.loading')}</Text>
                    </LiveRegion>
                  )}
                  {list.isError && (
                    <LoadErrorState title={t('adminArea.releaseNotes.loadError')} titleLevel={3} onRetry={() => void list.refetch()} />
                  )}
                  {list.isSuccess && notes.length === 0 && (
                    <EmptyState
                      icon={<Megaphone size={48} strokeWidth={1.5} aria-hidden="true" />}
                      headline={t('adminArea.releaseNotes.emptyTitle')}
                      description={t('adminArea.releaseNotes.emptyMessage')}
                      actionLabel={t('adminArea.releaseNotes.create.open')}
                      onAction={() => setCreating(true)}
                      headingLevel={3}
                    />
                  )}
                  {list.isSuccess && combinable.length >= 2 && (
                    <Inline gap="3" align="center" wrap>
                      <Text size="sm" color="muted" id="release-notes-combine-hint">
                        {t('adminArea.releaseNotes.combine.hint')}{' '}
                        {t('adminArea.releaseNotes.combine.selectedCount', { count: pickedIds.length })}
                      </Text>
                      <Button
                        variant="secondary"
                        onClick={askCombine}
                        disabled={pickedIds.length < 2}
                        aria-describedby="release-notes-combine-hint"
                      >
                        {t('adminArea.releaseNotes.combine.action', { count: pickedIds.length })}
                      </Button>
                    </Inline>
                  )}
                  {list.isSuccess && notes.length > 0 && (
                    <AdminTable caption={t('adminArea.releaseNotes.tableCaption')}>
                      <thead>
                        <tr>
                          <th scope="col" className="admin-release-notes__select">
                            <span className="admin-release-notes__sr">{t('adminArea.releaseNotes.combine.selectColumn')}</span>
                          </th>
                          <th scope="col">{t('adminArea.releaseNotes.columns.version')}</th>
                          <th scope="col">{t('adminArea.releaseNotes.columns.date')}</th>
                          <th scope="col">{t('adminArea.releaseNotes.columns.status')}</th>
                          <th scope="col">{t('adminArea.releaseNotes.columns.generation')}</th>
                          <th scope="col">{t('adminArea.releaseNotes.columns.recipients')}</th>
                          <th scope="col">{t('adminArea.releaseNotes.columns.actions')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {notes.map((note) => (
                          <tr key={note.id}>
                            <td className="admin-release-notes__select">
                              {isCombinable(note) && (
                                <Checkbox
                                  id={`combine-${note.id}`}
                                  label={
                                    <span className="admin-release-notes__sr">
                                      {t('adminArea.releaseNotes.combine.select', { version: note.appVersion ?? note.version })}
                                    </span>
                                  }
                                  checked={picked.has(note.id)}
                                  onChange={(e) => togglePicked(note.id, e.target.checked)}
                                />
                              )}
                            </td>
                            <th scope="row">
                              {note.appVersion ? (
                                <span className="admin-release-notes__version">
                                  <Text as="span" size="sm" weight="semibold">
                                    {t('appVersion.label', { version: note.appVersion })}
                                  </Text>
                                  <Text as="span" size="xs" color="muted">{note.version}</Text>
                                </span>
                              ) : (
                                note.version
                              )}
                            </th>
                            <td>{formatDate(note.deployedAt ?? note.createdAt, i18n.language)}</td>
                            <td>
                              <Badge tone={statusTone(note.status)}>{t(`adminArea.releaseNotes.status.${note.status}`)}</Badge>
                              {note.showInApp && <Badge tone="info">{t('adminArea.releaseNotes.inApp.badge')}</Badge>}
                              {note.combinedInto !== null && <Badge tone="neutral">{t('adminArea.releaseNotes.combine.badgeCombined')}</Badge>}
                              {note.combinedFrom.length > 0 && (
                                <Badge tone="info">{t('adminArea.releaseNotes.combine.badgeCombination', { count: note.combinedFrom.length })}</Badge>
                              )}
                            </td>
                            <td>
                              <Badge tone={generationTone(note.generationStatus)}>
                                {t(`adminArea.releaseNotes.generation.${note.generationStatus}`)}
                              </Badge>
                            </td>
                            <td>{formatNumber(note.deliveries.sent, i18n.language)}</td>
                            <td>
                              <Button
                                variant="secondary"
                                size="sm"
                                onClick={() => setSelectedId(note.id)}
                                aria-label={t('adminArea.releaseNotes.openLabel', { version: note.version })}
                              >
                                {t('adminArea.releaseNotes.open')}
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </AdminTable>
                  )}
                </Stack>
              </Section>
            )}
            <AdminConfirmDialog request={confirm} onClose={() => setConfirm(null)} errorMessage={combineError} />
          </AdminArea>
        </PageContent>
      }
    />
  );
}

export default AdminReleaseNotesPage;
